import { TranslateModule } from '@ngx-translate/core';
import {
  BadgeColors,
  BadgeComponent,
  CardComponent,
  DynamicTableComponent,
  InputComponent,
  SelectComponent,
  TableColumn,
} from 'shared';
import {
  Component,
  computed,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { CompanyService } from '../../../core/domain/company/company.service';
import { CountriesService } from '../../../core/domain/countries/countries.service';
import { getCountryFlagUrlByNumeric } from '../../../core/utils/country-flag.util';
import { CompanyDto } from '../../api/model/companyDto';
import { CompanyDetailsViewComponent } from './company-details-view/company-details-view.component';
import { CompanyStatusModalComponent } from './company-status-modal/company-status-modal.component';

@Component({
  selector: 'app-companies-list-page',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    BadgeComponent,
    CardComponent,
    DynamicTableComponent,
    InputComponent,
    SelectComponent,
    CompanyDetailsViewComponent,
    CompanyStatusModalComponent,
  ],
  templateUrl: './companies-list-page.component.html',
  styleUrl: './companies-list-page.component.scss',
})
export class CompaniesListPageComponent implements OnInit {
  @ViewChild('companyTemplate', { static: true })
  companyTemplate!: TemplateRef<CompanyDto>;

  @ViewChild('credentialsTemplate', { static: true })
  credentialsTemplate!: TemplateRef<CompanyDto>;

  @ViewChild(CompanyDetailsViewComponent)
  detailsView?: CompanyDetailsViewComponent;

  private readonly companyService = inject(CompanyService);
  private readonly countriesService = inject(CountriesService);

  searchTerm = model('');
  statusFilter = signal('approved');
  statusFilterControl = new FormControl('approved');
  credentialTypeFilter = signal('all');
  credentialTypeFilterControl = new FormControl('all');

  selectedCompanyDid = signal<string | null>(null);
  isDetailsViewOpen = signal(false);

  isRevokeModalOpen = signal(false);
  revokeModalCompanyName = signal('');
  revokeModalCredentialType = signal<string | null>(null);
  isProcessing = signal(false);
  pendingCredentialRevoke = signal<any>(null);

  constructor() {
    this.statusFilterControl.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.statusFilter.set(value);
      }
    });
    this.credentialTypeFilterControl.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.credentialTypeFilter.set(value);
      }
    });
  }

  filteredCompanies = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const status = this.statusFilter();
    const credentialType = this.credentialTypeFilter();
    let result = this.companyService.companies();

    if (term) {
      result = result.filter((company) => {
        const name = company.companyProfile?.name?.toLowerCase() || '';
        const did = company.did?.toLowerCase() || '';
        const country = company.companyProfile?.countryIso2?.toLowerCase() || '';
        const credentials = company.requestedCredentialTypes?.join(' ').toLowerCase() || '';

        return (
          name.includes(term) ||
          did.includes(term) ||
          country.includes(term) ||
          credentials.includes(term)
        );
      });
    }

    if (status !== 'all') {
      result = result.filter((company) => company.status === status);
    }

    if (credentialType !== 'all') {
      result = result.filter((company) => {
        if (credentialType === 'digitalIdentity') {
          return !!company.existingIdentityPresentation;
        }
        return company.requestedCredentialTypes?.includes(
          credentialType as CompanyDto.RequestedCredentialTypesEnum,
        );
      });
    }

    return result;
  });

  credentialBadgeColor = BadgeColors.blue;
  identityBadgeColor = BadgeColors.green;

  columns: TableColumn<CompanyDto>[] = [];

  statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'revoked', label: 'Revoked' },
  ];

  credentialTypeOptions = [
    { value: 'all', label: 'All Credentials' },
    { value: 'digitalIdentity', label: 'Digital Identity' },
    { value: CompanyDto.RequestedCredentialTypesEnum.GbaFacilityReportIssuer, label: 'GBAFacilityReportIssuer' },
    { value: CompanyDto.RequestedCredentialTypesEnum.GbaVerifiedCompany, label: 'GBAVerifiedCompany' },
    { value: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedVerifierCredential, label: 'GBAApprovedVerifier' },
    { value: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedCertifierCredential, label: 'GBAApprovedCertifier' },
    { value: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedCarbonCalculationPartnerCredential, label: 'GBAApprovedCarbonPartner' },
  ];

  ngOnInit(): void {
    this.columns = [
      {
        key: 'company',
        header: 'Company',
        width: '45%',
        cellTemplate: this.companyTemplate,
      },
      {
        key: 'credentials',
        header: 'Credentials',
        width: '55%',
        sortable: false,
        cellTemplate: this.credentialsTemplate,
      },
    ];
    this.companyService.fetchCompanies();
  }

  onRowClick(row: CompanyDto): void {
    this.openCompanyDetails(row);
  }

  openCompanyDetails(company: CompanyDto): void {
    this.selectedCompanyDid.set(company.did!);
    this.isDetailsViewOpen.set(true);
  }

  openRevokeModal(company: CompanyDto): void {
    this.selectedCompanyDid.set(company.did!);
    this.revokeModalCompanyName.set(company.companyProfile.name);
    this.isRevokeModalOpen.set(true);
  }

  onCredentialRevoke(event: { companyDid: string; credential: any }): void {
    const company = this.companies().find((c) => c.did === event.companyDid);

    if (!company) return;

    const credentialType = this.getCredentialTypeFromCredential(event.credential);

    this.selectedCompanyDid.set(event.companyDid);
    this.revokeModalCompanyName.set(company.companyProfile.name);
    this.revokeModalCredentialType.set(credentialType);
    this.pendingCredentialRevoke.set(event.credential);
    this.isRevokeModalOpen.set(true);
  }

  private getCredentialTypeFromCredential(credential: any): string {
    if (!credential?.type) return 'Unknown';
    const types = Array.isArray(credential.type) ? credential.type : [credential.type];
    const specificType = types.find((t: string) => t !== 'VerifiableCredential');
    return specificType || 'VerifiableCredential';
  }

  closeRevokeModal(): void {
    this.isRevokeModalOpen.set(false);
    this.revokeModalCredentialType.set(null);
    this.pendingCredentialRevoke.set(null);
  }

  confirmRevoke(): void {
    const companyDid = this.selectedCompanyDid();
    const credential = this.pendingCredentialRevoke();

    if (!companyDid) return;

    this.isProcessing.set(true);

    if (credential) {
      const credentialId = credential.id || credential.proof?.jwt;

      if (!credentialId) {
        console.error('No credential ID found for revocation');
        this.isProcessing.set(false);
        return;
      }

      this.companyService.revokeCredential(credentialId).subscribe({
        next: () => {
          this.detailsView?.markCredentialRevoked(credentialId, new Date().toISOString());
          this.closeRevokeModal();
          this.isProcessing.set(false);
          this.companyService.fetchCompanies();
        },
        error: (err) => {
          console.error('Error revoking credential:', err);
          this.isProcessing.set(false);
        },
      });
    } else {
      this.companyService.revokeCompany(companyDid).subscribe({
        next: () => {
          this.closeRevokeModal();
          this.isDetailsViewOpen.set(false);
          this.isProcessing.set(false);
          this.selectedCompanyDid.set(null);
          this.companyService.fetchCompanies();
        },
        error: (err) => {
          console.error('Error revoking company:', err);
          this.isProcessing.set(false);
        },
      });
    }
  }

  get companies() {
    return this.companyService.companies;
  }

  get loading() {
    return this.companyService.loading;
  }

  get error() {
    return this.companyService.error;
  }

  getFlagUrl(countryIso2: string | undefined): string | null {
    if (!countryIso2) return null;
    const country = this.countriesService.getCountryByIso2(countryIso2);
    if (!country) return null;
    return getCountryFlagUrlByNumeric(country.codeISONumeric);
  }

  truncateDid(did: string): string {
    if (!did || did.length <= 30) return did;
    return `${did.slice(0, 15)}...${did.slice(-10)}`;
  }

  getCredentialLabel(type: CompanyDto.RequestedCredentialTypesEnum): string {
    return type;
  }

  hasDigitalIdentity(company: CompanyDto): boolean {
    return !!company.existingIdentityPresentation;
  }
}
